/**
 * Stepper do histórico do processo (TB02130) no modal de detalhes da OS.
 * Visual baseado em ícones SVG (done/active), linhas absolutas; clique exibe TB02130_OBS à direita.
 */

import React, { useState, useRef, useLayoutEffect } from 'react';
import './OrderDetailsHistoricoProcesso.css';

const MESES_PT = ['jan', 'fev', 'mar', 'abr', 'mai', 'jun', 'jul', 'ago', 'set', 'out', 'nov', 'dez'];

const PURPLE = '#7c4dff';
const GRAY_LINE = '#c8c8c8';
const GRAY_TEXT = '#888';
const GRAY_CIRCLE = '#bdbdbd';
const PURPLE_LIGHT = '#9c7bff';

const ICON_DONE = Math.round(12 * 0.65); /* 35% menor que 12 */
const ICON_ACTIVE = 23;
const ICON_PENDING = Math.round(12 * 0.65);
const ROW_GAP = 20;
const COL_W = ICON_ACTIVE;
/** Altura extra por linha quando "Mostrar detalhes" está ativo (marginTop 3 + linha data/usuário) */
const EXTRA_DETAIL_HEIGHT = 21;

function iconSize(status) {
  return status === 'active' ? ICON_ACTIVE : ICON_DONE;
}

function StepIcon({ status }) {
  if (status === 'done') {
    const s = ICON_DONE;
    return (
      <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`} fill="none" aria-hidden>
        <circle cx={s / 2} cy={s / 2} r={s / 2} fill={PURPLE} />
      </svg>
    );
  }
  if (status === 'active') {
    const s = ICON_ACTIVE;
    const r = s / 2;
    return (
      <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`} fill="none" aria-hidden>
        <circle cx={r} cy={r} r={r - 1.5} stroke={PURPLE} strokeWidth="2" fill="white" />
        <circle cx={r} cy={r} r={r * 0.52} fill={PURPLE} />
      </svg>
    );
  }
  const s = ICON_PENDING;
  return (
    <svg width={s} height={s} viewBox={`0 0 ${s} ${s}`} fill="none" aria-hidden>
      <circle cx={s / 2} cy={s / 2} r={s / 2 - 1} stroke={GRAY_CIRCLE} strokeWidth="1.5" fill="white" />
    </svg>
  );
}

/** Formata data/hora sem conversão de fuso: exibe o valor original (UTC) do dado. */
function formatDataUser(dataStr, user) {
  if (!dataStr) return user || '';
  const d = new Date(dataStr);
  if (isNaN(d.getTime())) return user || '';
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mes = MESES_PT[d.getUTCMonth()] || '';
  const hh = String(d.getUTCHours()).padStart(2, '0');
  const mm = String(d.getUTCMinutes()).padStart(2, '0');
  const dataPart = `${dd} ${mes} ${hh}:${mm}`;
  return user ? `${dataPart} | ${user}` : dataPart;
}

export function OrderDetailsHistoricoProcesso({ steps, loading, error }) {
  const [selectedIndex, setSelectedIndex] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const stepperRef = useRef(null);
  const lastIndex = steps && steps.length > 0 ? steps.length - 1 : -1;

  useLayoutEffect(() => {
    if (!stepperRef.current || !steps?.length) return;
    const el = stepperRef.current;
    const scrollToEnd = () => {
      el.scrollTop = el.scrollHeight - el.clientHeight;
    };
    scrollToEnd();
    const t = requestAnimationFrame(scrollToEnd);
    return () => cancelAnimationFrame(t);
  }, [steps?.length]);

  if (loading) {
    return (
      <div className="order-details-historico">
        <div className="order-details-historico-loading">Carregando histórico…</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="order-details-historico">
        <div className="order-details-historico-error">{error}</div>
      </div>
    );
  }

  if (!steps || steps.length === 0) {
    return (
      <div className="order-details-historico">
        <div className="order-details-historico-empty">Nenhum registro no histórico do processo.</div>
      </div>
    );
  }

  const getStatus = (index) => (index === lastIndex ? 'active' : 'done');
  const rowHeight = (status, withDetails) => {
    const base = status === 'active' ? Math.max(ICON_ACTIVE, 18) + 18 : Math.max(iconSize(status), 18);
    return base + (withDetails ? EXTRA_DETAIL_HEIGHT : 0);
  };
  const centerOf = (i, withDetails) => {
    let top = 0;
    for (let j = 0; j < i; j++) {
      top += rowHeight(getStatus(j), withDetails) + ROW_GAP;
    }
    const iSz = iconSize(getStatus(i));
    const vertOffset = Math.max(0, (18 - iSz) / 2);
    return top + vertOffset + iSz / 2;
  };

  const selectedStep = selectedIndex != null ? steps[selectedIndex] : null;
  const obsText = selectedStep == null
    ? 'Clique em uma etapa para ver as observações.'
    : (selectedStep.TB02130_OBS?.trim() || 'Não há observações.');

  return (
    <div className="order-details-historico">
      <div className="order-details-historico-body">
        <div ref={stepperRef} className="order-details-historico-stepper" style={{ position: 'relative' }}>
          {/* Linhas entre ícones (com showDetails para altura correta quando detalhes visíveis) */}
          {steps.map((step, index) => {
            if (index === steps.length - 1) return null;
            const isDone = getStatus(index) === 'done' || index < lastIndex;
            const fromCenter = centerOf(index, showDetails);
            const toCenter = centerOf(index + 1, showDetails);
            const lineHeight = Math.max(0, toCenter - fromCenter);
            return (
              <div
                key={`line-${index}`}
                style={{
                  position: 'absolute',
                  left: COL_W / 2 - 1,
                  top: fromCenter,
                  height: lineHeight,
                  width: 2,
                  background: isDone ? PURPLE : GRAY_LINE,
                  borderRadius: 1,
                  zIndex: 0,
                }}
              />
            );
          })}

          {/* Rows dos steps */}
          {steps.map((step, index) => {
            const status = getStatus(index);
            const isLast = index === lastIndex;
            const isActive = status === 'active';
            const isSelected = selectedIndex === index;
            const iSize = iconSize(status);
            const iconVertOffset = Math.max(0, (18 - iSize) / 2);

            return (
              <button
                key={index}
                type="button"
                className={`order-details-historico-step ${isSelected ? 'order-details-historico-step--selected' : ''}`}
                onClick={() => setSelectedIndex(index)}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  marginBottom: isLast ? 0 : ROW_GAP,
                  position: 'relative',
                  zIndex: 1,
                  width: '100%',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  cursor: 'pointer',
                  textAlign: 'left',
                }}
              >
                <div
                  style={{
                    width: COL_W,
                    flexShrink: 0,
                    marginRight: 12,
                    display: 'flex',
                    justifyContent: 'center',
                    paddingTop: iconVertOffset,
                  }}
                >
                  <StepIcon status={status} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontSize: 14,
                      fontWeight: isActive ? 700 : 400,
                      color: isActive ? PURPLE : index <= lastIndex ? '#555' : GRAY_TEXT,
                      lineHeight: '18px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                    }}
                  >
                    <span>{step.TB02130_NOME}</span>
                    {isSelected && (
                      <span
                        style={{
                          width: 4,
                          height: 4,
                          borderRadius: '50%',
                          background: '#1a1a1a',
                          flexShrink: 0,
                        }}
                        aria-hidden
                      />
                    )}
                  </div>
                  {(isActive || showDetails) && (
                    <div style={{ marginTop: 3 }}>
                      <span style={{ fontSize: 10, color: GRAY_TEXT }}>
                        {formatDataUser(step.TB02130_DATA, step.TB02130_USER)}
                      </span>
                    </div>
                  )}
                </div>
              </button>
            );
          })}

          <button
            type="button"
            onClick={() => setShowDetails((v) => !v)}
            className="order-details-historico-toggle-details"
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              marginTop: 6,
              marginLeft: COL_W + 12,
              color: 'rgba(0, 0, 0, 1)',
              fontSize: 10,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 3,
              fontWeight: 400,
            }}
          >
            {showDetails ? 'Ocultar detalhes' : 'Mostrar detalhes'}
            <svg
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none"
              style={{
                transform: showDetails ? 'rotate(180deg)' : 'rotate(0deg)',
                transition: 'transform 0.2s',
              }}
              aria-hidden
            >
              <path
                d="M2 4l4 4 4-4"
                stroke="rgba(0, 0, 0, 1)"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>
        <div className="order-details-historico-obs-panel">
          {selectedStep == null ? (
            <div className="order-details-historico-obs-placeholder">
              <i className="bi bi-journal-text" aria-hidden />
              <h3>Selecione uma etapa para ver as observações</h3>
              <p>Clique em uma etapa do histórico ao lado para exibir as observações</p>
            </div>
          ) : (
            <p className="order-details-historico-obs-content">{obsText}</p>
          )}
        </div>
      </div>
    </div>
  );
}
